import React from 'react'
import {
  Alert,
  Empty,
  Input,
  Pagination,
  Segmented,
} from '@surecompanies/core_components'
import { SearchOutlined } from '@ant-design/icons'

import { UpdatingBar } from '../Skeletons'
import { colors } from '../tokens'
import { gridMinWidth, ReviewQueueColumn, withChipCounts } from './helpers'
import TableHead from './TableHead'
import { ReviewQueuePanelState } from './useReviewQueuePanel'
import type { ReviewQueueFilter } from '../../../api/reconciliation-v2-api-client'
import styles from './ReviewQueue.module.scss'

/**
 * Chrome shared by both Required-action tabs: the sticky filter bar, the
 * sticky header, the first-load skeleton / empty / error states and the pager.
 *
 * Both tabs are the same KIND of surface over two different wire shapes, so
 * the shell is shared and only the columns, the filter vocabulary, the copy and
 * the rows differ — those all arrive as props from the tab that owns them.
 * The filter bar lives INSIDE the scrollport with the header so both stay
 * reachable while a long page scrolls; the pager sits outside it.
 */

interface QueuePanelCopy {
  emptyDescription: string
  searchPlaceholder: string
  errorMessage: string
}

interface QueuePanelProps<F extends ReviewQueueFilter> {
  panel: ReviewQueuePanelState<F>
  copy: QueuePanelCopy
  columns: readonly ReviewQueueColumn[]
  gridTemplateColumns: string
  /**
   * The chips, UNCOUNTED — this shell decorates them with `panel.counts`. They
   * arrive bare because the same static array is what ./url-state derives its
   * `allowedFilters` from, and a counted label must never reach the URL.
   */
  filterOptions: readonly { label: string; value: F }[]
  /**
   * The renderable rows of the current page. Empty means "nothing to show" —
   * which is NOT the same as `panel.items` being empty: a row belonging to the
   * other arm cannot be rendered here, and counting it would leave a headed
   * table with a blank body and no explanation.
   */
  rows: readonly React.ReactNode[]
}

const QueuePanel = <F extends ReviewQueueFilter>({
  panel,
  copy,
  columns,
  gridTemplateColumns,
  filterOptions,
  rows,
}: QueuePanelProps<F>) => (
  <>
    <div className={styles.wrap}>
      {/* Full skeleton only on the very first load. Background refetches keep
          the current rows on screen and show the subtle updating bar instead.

          `showSkeleton` must stay derived from `isLoading`, NEVER `isFetching`:
          the refetch after an allocate/post is an invalidation of an unchanged
          query key, so widening this would blank the whole list every time an
          operator clears a row — the exact "lost my place" complaint issue 06
          set out to fix. No test guards it, because the refetch produces no
          observable intermediate render in jsdom; see the note in
          ./refetch-stability.test.tsx. */}
      <UpdatingBar $active={panel.isFetching && !panel.isLoading} />

      {/* Outside the scrollport, so a sideways scroll of a wide table moves
          the columns and never the search box or the chips. */}
      <div className={styles.toolbar}>
        <Input
          placeholder={copy.searchPlaceholder}
          value={panel.search}
          onChange={(e) => panel.setSearch(e.target.value)}
          onPressEnter={panel.applySearch}
          prefix={<SearchOutlined style={{ color: colors.text.faint }} />}
          allowClear
          onClear={panel.clearSearch}
          style={{ flex: 'none', width: 300 }}
        />
        <div className={styles.filterScroll}>
          {/* Counted here rather than in either tab: both want the
              same treatment, and `panel.counts` is already to hand. "All"
              gets the SUM of the map — NOT `panel.total`, which is scoped to
              the selected chip and would read "All (3)" beside "Refund (3)".
              See withChipCounts. */}
          <Segmented
            value={panel.filter}
            onChange={(value) => panel.changeFilter(value as F)}
            options={
              withChipCounts(filterOptions, panel.counts) as {
                label: string
                value: F
              }[]
            }
          />
        </div>
      </div>

      {/* The list's scrollport. Survives an allocate/post: the refetch that
          follows is an INVALIDATION of an unchanged query key, so `isLoading`
          stays false, the skeleton branch is never taken and this node is
          reconciled rather than remounted — which is what keeps the operator's
          scroll position (spec D9, issue 06). `data-testid` exists to pin that:
          see ./refetch-stability.test.tsx. */}
      <div className={styles.scrollBox} data-testid="review-queue-scrollport">
        {panel.showFullError ? (
          <div className={styles.errorWrap}>
            <Alert
              type="error"
              message={copy.errorMessage}
              description="Could not retrieve the required-action items. Please refresh the page."
            />
          </div>
        ) : (
          <div
            className={styles.gridBody}
            style={{ minWidth: gridMinWidth(columns) }}
          >
            <TableHead
              columns={columns}
              gridTemplateColumns={gridTemplateColumns}
            />
            {panel.showSkeleton ? (
              Array.from({ length: Math.min(panel.limit, 12) }).map((_, i) => (
                <div
                  key={i}
                  className={styles.skeletonRow}
                  style={{ gridTemplateColumns }}
                >
                  {columns.map((col) => (
                    <div key={col.label} className={styles.skeletonCell} />
                  ))}
                </div>
              ))
            ) : rows.length === 0 ? (
              <div className={styles.emptyWrap}>
                <Empty description={copy.emptyDescription} />
              </div>
            ) : (
              rows
            )}
          </div>
        )}
      </div>
    </div>

    {panel.total > 0 && (
      <div className={styles.pagBar}>
        <Pagination
          total={panel.total}
          current={panel.page}
          pageSize={panel.limit}
          showSizeChanger
          pageSizeOptions={['25', '50', '100']}
          onChange={panel.onPaginationChange}
        />
      </div>
    )}
  </>
)

export default QueuePanel
